import React from "react";
import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  TextField,
} from "@material-ui/core";
import { useTheme } from "@material-ui/styles";
import { assetIds, RichTextfield, ThemedAsset } from "nemuru-components";
import { Controller, useForm } from "react-hook-form";
import Text from "../../../../../shared/components/Text";
import MainButton from "../../../../../shared/components/MainButton";
import IconTitle from "../../../../../shared/components/IconTitle";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { objectTypesConfigToArray } from "../../../../../../utils/objectTypesConfigToArray";
import {
  netIncomeTypesConfig,
  netIncomeTypesWithoutOtherConfig,
} from "../../config";
import { parseStringAmountWithUnitToNumber } from "../../../../../../utils/formaters";
import { ICON_TITLE_HEIGHT } from "../../../../../../config/config";
import { employmentStatusTypesConfig } from "../../../../kyc/ProfessionalData/config";

const useStyles = makeStyles((theme) => ({
  toggleButtonroot: {
    width: "100%",
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.primary,
    border: "none",
  },
  toggleButtonselected: {
    backgroundColor: `${theme.palette.text.primary} !important`,
    color: `${theme.palette.common.white} !important`,
  },
}));

export default function EditIncomeForm({
  onSubmitForm,
  data,
  disableNetIncomeType,
  employmentStatus,
  disableOthers,
}) {
  const classes = useStyles();
  const { palette, shape } = useTheme();
  const { register, getValues, handleSubmit, errors, control, watch } = useForm(
    {
      defaultValues: {
        netIncomeType: data?.netIncomeType || "",
        monthlyIncome: data?.monthlyIncome || "",
        numberOfPaymentsPerYear: data?.numberOfPaymentsPerYear || "",
      },
      reValidateMode: "onBlur",
    }
  );

  const netIncomeTypes =
    disableOthers === true
      ? netIncomeTypesWithoutOtherConfig
      : netIncomeTypesConfig;

  const SetIncomeTypeByEmploymentStatus = (employmentStatus) => {
    switch (employmentStatus) {
      case employmentStatusTypesConfig.PENSIONER.value:
        return {
          PENSION: netIncomeTypes.PENSION,
        };
      default:
        return netIncomeTypes;
    }
  };

  const validateMinimumIncomeValueAndReturnErrorMessage = (value) => {
    const minimumIncome = 300;
    const incomeInt = parseStringAmountWithUnitToNumber(value);
    if (incomeInt < minimumIncome) {
      return (
        <Text
          tId={"INCOME_AMOUNT_GREATER_THAN_VALUE"}
          dynamicDataTexts={[minimumIncome]}
        />
      );
    }
  };

  return (
    <>
      <Box className="appear-anim">
        <IconTitle
          icon={
            <ThemedAsset
              id={assetIds.ICON_MONEY_INCOMES}
              width={ICON_TITLE_HEIGHT}
              height={ICON_TITLE_HEIGHT}
              style={{ color: palette.secondary.main }}
            />
          }
          title={<Text tId="FINANCIAL_DATA" />}
          subtitle={<Text tId="PROVIDE_MAIN_INCOME_SOURCE" />}
        />
        {!disableNetIncomeType && (
          <FormControl fullWidth>
            <InputLabel id="netIncomeType">
              <Text tId="NET_INCOME_TYPE" />
            </InputLabel>
            <Controller
              name="netIncomeType"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: <Text tId={"REQUIRED_FIELD"} />,
                },
              }}
              render={(props) => (
                <>
                  <Select
                    MenuProps={{
                      MenuListProps: {
                        "aria-label": "Select net income type",
                      },
                    }}
                    onChange={(e) => props.onChange(e.target.value)}
                    value={props.value}
                    error={errors.netIncomeType ? true : false}
                  >
                    {objectTypesConfigToArray(
                      SetIncomeTypeByEmploymentStatus(employmentStatus)
                    ).map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.display}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText style={{ color: palette.error.main }}>
                    {errors.netIncomeType?.message}
                  </FormHelperText>
                </>
              )}
            />
          </FormControl>
        )}
        <RichTextfield
          defaultValue={parseStringAmountWithUnitToNumber(data?.monthlyIncome)}
          allowDecimals
          hideIcon
          unitName={"€"}
          fullWidth
          label={<Text tId={"MONTHLY_INCOME"} />}
          id="monthlyIncome"
          name="monthlyIncome"
          InputLabelProps={{ shrink: true }}
          inputProps={{
            ref: register({
              required: {
                value: true,
                message: <Text tId={"REQUIRED_FIELD"} />,
              },
              validate: {
                validate: (value) =>
                  validateMinimumIncomeValueAndReturnErrorMessage(value),
              },
            }),
          }}
          error={errors.monthlyIncome ? true : false}
          helperText={errors.monthlyIncome?.message}
        />
        <FormControl fullWidth>
          <Box display="flex" alignItems="flex-end" mb={2}>
            <Box flexGrow={1}>
              <FormLabel id="numberOfPaymentsPerYear">
                <Text tId="NUMBER_OF_PAYMENTS_PER_YEAR" />
              </FormLabel>
            </Box>
            <Box>
              <TextField
                type="number"
                id="numberOfPaymentsPerYear"
                name="numberOfPaymentsPerYear"
                style={{
                  width: "50px",
                  margin: 0,
                }}
                error={errors.numberOfPaymentsPerYear ? true : false}
                inputProps={{
                  "aria-label": "Number of payments per year",
                  ref: register({}),
                }}
              />
              <FormHelperText style={{ color: palette.error.main }}>
                {errors.numberOfPaymentsPerYear?.message}
              </FormHelperText>
            </Box>
          </Box>
          <Controller
            name="numberOfPaymentsPerYear"
            control={control}
            rules={{
              required: {
                value: true,
                message: <Text tId={"REQUIRED_FIELD"} />,
              },
              min: {
                value: 1,
                message: <Text tId={"INVALID"} />,
              },
              max: {
                value: 99,
                message: <Text tId={"INVALID"} />,
              },
            }}
            render={(props) => (
              <>
                <ToggleButtonGroup
                  style={{
                    width: "100%",
                    borderRadius: shape.borderRadius,
                  }}
                  value={watch("numberOfPaymentsPerYear")}
                  onChange={(e) => props.onChange(e.target.value)}
                  exclusive
                  aria-label="text alignment"
                >
                  <ToggleButton
                    value="12"
                    classes={{
                      root: classes.toggleButtonroot,
                      selected: classes.toggleButtonselected,
                    }}
                  >
                    12
                  </ToggleButton>
                  <ToggleButton
                    value="14"
                    classes={{
                      root: classes.toggleButtonroot,
                      selected: classes.toggleButtonselected,
                    }}
                  >
                    14
                  </ToggleButton>
                  <ToggleButton
                    value="15"
                    classes={{
                      root: classes.toggleButtonroot,
                      selected: classes.toggleButtonselected,
                    }}
                  >
                    15
                  </ToggleButton>
                </ToggleButtonGroup>
              </>
            )}
          />
        </FormControl>
      </Box>
      <Box>
        <MainButton onClick={handleSubmit(() => onSubmitForm(getValues()))} />
      </Box>
    </>
  );
}
